import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { useProgressPolling } from '~/components/Accommodation/hooks/useProgressPolling';
import Spinner from '~/components/Common/Spinner';

import { useDedupePropertyImages } from '~/hooks/apis/accommodation/useDedupePropertyImages';
import { propertyImagesQueryKeys } from '~/hooks/apis/accommodation/useGetPropertyImages';
import { roomImagesQueryKeys } from '~/hooks/apis/accommodation/useGetRoomImages';

import { ProgressStatusResponse, getProgressStatuses } from '~/services/AccommodationService';

export default function AccommodationPropertyActionsSection() {
  const { propertyId = '' } = useParams<{ propertyId: string }>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const dedupeMutation = useDedupePropertyImages();
  const dedupeMutateAsyncRef = useRef(dedupeMutation.mutateAsync);
  dedupeMutateAsyncRef.current = dedupeMutation.mutateAsync;

  const apiCall = useCallback(() => dedupeMutateAsyncRef.current({ propertyId }), [propertyId]);
  const onClose = useCallback(() => setIsDialogOpen(false), []);
  const { isLoading, isComplete, statusMessage, errorMessage, statuses, runStartJob, resetAndClose } =
    useProgressPolling<ProgressStatusResponse>({
      apiCall,
      getStatus: getProgressStatuses,
      onClose,
      options: { maxTime: 60000 * 5, pollInterval: 1000 },
    });

  const imageDedupeStatus = statuses.find((status) => String(status.type) === 'imageDedupe');

  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (!isComplete) {
      hasNotifiedRef.current = false;
      return;
    }
    if (hasNotifiedRef.current) return;
    hasNotifiedRef.current = true;

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: propertyImagesQueryKeys.detail(propertyId) }),
      queryClient.invalidateQueries({ queryKey: roomImagesQueryKeys.all }),
    ]);
    enqueueSnackbar('Image dedupe complete', { variant: 'success' });
  }, [enqueueSnackbar, isComplete, propertyId, queryClient]);

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="h6">Image dedupe</Typography>
          <Typography variant="body2" color="text.secondary">
            Store duplicate links for property images and room images. Manual hidden flags are unchanged.
          </Typography>
          <Button variant="contained" onClick={() => setIsDialogOpen(true)} disabled={!propertyId}>
            Dedupe images
          </Button>
        </Stack>
      </Paper>

      <Dialog open={isDialogOpen} onClose={resetAndClose}>
        <DialogTitle>Dedupe images?</DialogTitle>
        <DialogContent sx={{ minWidth: 420 }}>
          <Stack spacing={2}>
            {!isLoading && !isComplete && (
              <DialogContentText>
                This will collect comparison data, then store duplicate links for this property's images and each room's
                images. It will not change manual hidden flags.
              </DialogContentText>
            )}
            {imageDedupeStatus && (
              <DialogContentText>{imageDedupeStatus.message ?? imageDedupeStatus.state}</DialogContentText>
            )}
            {isComplete && statusMessage && <DialogContentText color="primary">{statusMessage}</DialogContentText>}
            {errorMessage && <DialogContentText color="error">{errorMessage}</DialogContentText>}
          </Stack>
        </DialogContent>
        {!isComplete && (
          <DialogActions>
            <Button variant="text" onClick={resetAndClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => runStartJob()} disabled={isLoading}>
              {isLoading ? <Spinner size={15} inline /> : 'Dedupe images'}
            </Button>
          </DialogActions>
        )}
        {isComplete && (
          <DialogActions>
            <Button variant="contained" onClick={resetAndClose} disabled={isLoading}>
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
